import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiFile, BiPaperclip, BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { useUploadToCDNMutation } from '@/app/services/chatbot-rtk.services';
import { PackageRTKService, useAddMessageMutation, useDisputeTransactionMutation, useUpdateUserStatusMutation } from '@/app/services/package-rtk.service';
import { ScenarioRTKService } from '@/app/services/scenario-rtk.service';
import { BannerHelper } from '@/components/common/BannerToast';
import { confirmWithDialog } from '@/components/common/confirmation/confirm-dialog';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { selectUseId } from '@/features/auth/auth.slice';

import ScenarioSide from './video-scenario-side';

const MAX_SIZE = 30 * 1024 * 1024; // 30MB
const formatExtensions = [
  '.png',
  '.jpeg',
  '.jpg',
  '.gif',
  '.mp3',
  '.aac',
  '.ogg',
  '.wav',
  '.mp4',
  '.mpeg',
  '.webm',
  '.ogg',
  '.mov',
  '.m4v',
  '.mkv',
  '.avi',
  '.3gp',
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.rtf',
  '.xls',
  '.xlsx',
  '.csv',
  '.ppt',
  '.pptx',
  '.zip',
  '.rar',
  '.md',
  '.json',
  '.xml',
];

const formatSmartDate = (dateString: string, t: (key: string) => string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (messageDate.getTime() === today.getTime()) {
    return `${t('common/today')} ${time}`;
  }
  if (messageDate.getTime() === yesterday.getTime()) {
    return `${t('common/yesterday')} ${time}`;
  }

  const options: Intl.DateTimeFormatOptions =
    date.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };

  return `${date.toLocaleDateString([], options)} ${time}`;
};

const MessageBubble = ({ msg, isUser, isBuyer }: { msg: any; isUser: boolean; isBuyer: boolean }) => {
  let alignmentClass = '';
  if (msg.sender === 'ADMIN') {
    alignmentClass = 'tw-justify-center tw-self-center';
  } else if (isUser) {
    alignmentClass = 'tw-self-end';
  } else {
    alignmentClass = 'tw-self-start';
  }

  const { t } = useTranslation();

  // Determine sender label
  const getSenderLabel = () => {
    if (msg.sender === 'ADMIN') return null;
    if (isUser) return t('marketplace/messages/you');
    return isBuyer ? t('marketplace/messages/buyer') : t('marketplace/messages/seller');
  };

  const senderLabel = getSenderLabel();

  // Helper to detect file type

  const renderContent = () => {
    const value = msg.message;
    if (typeof value !== 'string') return <p className="tw-text-sm">{String(value)}</p>;
    // CDN file detection: crude check for URL and extension
    const isUrl = value.startsWith('http') || value.startsWith('/cdn') || value.startsWith('https://cdn');
    if (!isUrl) return <p className="tw-text-sm tw-whitespace-pre-wrap">{value}</p>;

    const lower = value.toLowerCase();
    if (lower.match(/\.(png|jpe?g|gif|webp)$/)) {
      return (
        <div>
          <img src={value} alt="uploaded" className="tw-max-w-full tw-max-h-60 tw-rounded-lg tw-mb-1" />
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-flex tw-text-sm tw-items-center tw-gap-2 tw-text-gray-800 tw-underline tw-break-all tw-mb-1"
          >
            {value}
          </a>
        </div>
      );
    }
    if (lower.match(/\.(mp4|webm|ogg|mov|m4v|mkv|avi|3gp|mpeg)$/)) {
      const filename = value.split('/').pop() || 'Video file';
      return (
        <div>
          <video src={value} controls className="tw-max-w-full tw-items-center tw-max-h-60 tw-rounded-lg tw-mb-1" aria-label={`Video: ${filename}`}>
            <track kind="captions" src="" label="Captions" default />
          </video>
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-flex tw-text-sm tw-items-center tw-gap-2 tw-text-gray-800 tw-underline tw-break-all tw-mb-1"
          >
            {value}
          </a>
        </div>
      );
    }
    if (lower.match(/\.(mp3|aac|wav|ogg)$/)) {
      const filename = value.split('/').pop() || 'Audio file';
      return (
        <div className="tw-flex tw-flex-col tw-gap-1">
          <audio src={value} controls className="tw-w-full tw-min-w-[300px]" aria-label={`Audio: ${filename}`}>
            <track kind="captions" src="" label="Captions" />
            <p>
              Your browser does not support the audio element. <a href={value}>Download audio</a>
            </p>
          </audio>
          <span className="tw-text-xs tw-text-gray-500">{filename}</span>
        </div>
      );
    }
    // fallback: generic file (any other extension or unknown extension)
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="tw-flex tw-text-sm tw-items-center tw-gap-2 tw-text-gray-800 tw-underline tw-break-all tw-mb-1"
      >
        <BiFile size={24} />
        {value.split('/').pop() || 'File'}
      </a>
    );
  };

  return (
    <div className={`tw-flex tw-flex-col tw-max-w-[70%] ${alignmentClass}`}>
      {senderLabel && (
        <span className={`tw-text-xs tw-font-medium tw-mb-1 ${isUser ? 'tw-text-right tw-text-blue-600' : 'tw-text-left tw-text-gray-600'}`}>
          {senderLabel} | {formatSmartDate(msg.date_time, t)}
        </span>
      )}
      <div
        className={`tw-rounded-2xl tw-px-4 tw-py-2 tw-shadow ${
          msg.sender === 'ADMIN'
            ? 'tw-bg-yellow-100 tw-text-gray-900'
            : isUser
            ? 'tw-bg-blue-500 tw-text-white'
            : 'tw-bg-white tw-text-gray-800 tw-border tw-border-gray-200'
        }`}
      >
        {renderContent()}
      </div>
    </div>
  );
};

const MessagesSkeleton = () => (
  <div className="tw-flex tw-flex-col tw-flex-1 tw-overflow-y-auto tw-p-6 tw-space-y-4">
    {[...Array(5)].map((_, i) => {
      const isUser = i % 2 === 1;
      return (
        <div key={i} className={`tw-flex ${isUser ? 'tw-justify-end' : 'tw-justify-start'} tw-w-full`}>
          <div
            className={`tw-h-6 tw-rounded-2xl tw-animate-pulse 
              ${isUser ? 'tw-bg-blue-200 tw-w-32' : 'tw-bg-gray-200 tw-w-40'}`}
          />
        </div>
      );
    })}
  </div>
);

export const VideoScenarioMessages = ({
  transactionId,
  setShowMessages,
  data,
  isLoading,
  isError,
  isSuccess,
  refetch,
  showMessages,
}: {
  transactionId: string;
  setShowMessages: (value: boolean) => void;
  data: any;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => void;
  showMessages: boolean;
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);

  const isDisabled = data?.payment_status === 'DISPUTE' || data?.buyer_transaction_status === 'FINISHED';
  const isBuyer = data?.seller === userId;
  const [addMessage, addMessageResult] = useAddMessageMutation();
  const [updateStatus, updateStatusResult] = useUpdateUserStatusMutation();
  const [cdnUpload, cdnUploadResults] = useUploadToCDNMutation();

  const [dispute, disputeResult] = useDisputeTransactionMutation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const schema = Yup.object({
    message: Yup.string().required(t('AlertsTypeConstant/messageRequired')),
  });

  const {
    reset,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<any>({
    defaultValues: { message: '' },
    resolver: yupResolver(schema),
  });

  const handleScenarioAction = async (action: 'finish' | 'dispute') => {
    if (action === 'finish') {
      await updateStatus({ transaction_id: transactionId, status: 'FINISHED' }).unwrap();
      toast.success(t('marketplace/messages/finishedMessage'));
    } else {
      await dispute(transactionId).unwrap();
      toast.success(t('marketplace/messages/disputeMessage'));
    }
    refetch();
    ScenarioRTKService.util.invalidateTags(['GetScenarioDetail']);
    PackageRTKService.util.invalidateTags(['GetTransaction']);
    setShowMessages(false);
  };

  const handleFinishClick = () => {
    confirmWithDialog(t('marketplace/messages/finishConfirm'), {
      confirmButtonText: 'marketplace/messages/finish',
      cancelButtonText: 'Wizard/general/key5',
      confirmButtonStyle: 'primary',
      cancelButtonStyle: 'outline',
      description: t('marketplace/messages/finishDescription'),
    }).then((confirmed: any) => {
      if (confirmed) {
        handleScenarioAction('finish');
      }
    });
  };

  const handleDisputeClick = () => {
    confirmWithDialog(t('marketplace/messages/disputeConfirm'), {
      confirmButtonText: 'marketplace/messages/dispute',
      cancelButtonText: 'Wizard/general/key5',
      confirmButtonStyle: 'danger',
      cancelButtonStyle: 'outline',
    }).then((confirmed: any) => {
      if (confirmed) {
        handleScenarioAction('dispute');
      }
    });
  };

  const handleUpload = (file: File | null) => {
    if (file) {
      // File size validation
      if (file.size > MAX_SIZE) {
        toast.warn('marketplace/messages/maxFileSize');
        return;
      }

      const formData = new FormData();
      formData.append('files', file, userId + '_' + file.name);
      formData.append(
        'data',
        JSON.stringify({
          orginalName: file.name,
          type: file.type,
          size: file.size,
        })
      );

      cdnUpload(formData)
        .unwrap()
        .then(value => {
          if (value[0]?.is_successful) {
            const path = value[0].cdnPath;
            onSubmit({ message: path });
          }
        });
    }
  };

  const onSubmit = (formData: any) => {
    addMessage({ transactionId, message: formData.message })
      .unwrap()
      .then(() => {
        reset({ message: '' });
      });
  };

  useEffect(() => {
    setTimeout(() => {
      wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 50);
  }, [showMessages, data?.transaction_data?.messages?.length, isLoading]);

  let content;
  if (isLoading) content = <MessagesSkeleton />;
  else if (isError) content = <PSButton id="btn-message-refetch" type="button" onClick={refetch} title="Retry" />;
  else if (isSuccess) {
    content = (
      <div ref={wrapperRef} className="tw-flex tw-flex-col tw-flex-1 tw-overflow-y-auto tw-bg-gray-50">
        {/* Info banner explaining the panel */}
        <div className="tw-p-3">
          <BannerHelper message={`${isBuyer ? t('marketplace/messages/panelDescBuyer') : t('marketplace/messages/panelDescSeller')}`} />
        </div>

        {/* Messages */}
        <div className="tw-flex tw-flex-col tw-flex-1 tw-p-6 tw-pt-0 tw-space-y-4">
          {data?.transaction_data?.messages?.length ? (
            data?.transaction_data?.messages.map((msg: any, idx: number) => (
              <MessageBubble key={idx} msg={msg} isUser={msg.sender === userId} isBuyer={isBuyer} />
            ))
          ) : (
            <p className="tw-text-gray-400 tw-text-sm tw-text-center">{t('marketplace/messages/noMessages')}</p>
          )}
          {data?.payment_status === 'DISPUTE' && (
            <div className="tw-bg-red-100 tw-text-red-800 tw-text-center tw-py-2 tw-px-4 tw-mx-4 tw-rounded-lg tw-mb-2 tw-flex tw-items-center tw-justify-center tw-gap-2">
              <span>{t('marketplace/messages/disputeBanner')}</span>
            </div>
          )}
          {data?.buyer_transaction_status === 'FINISHED' && (
            <div className="tw-bg-orange-100 tw-text-orange-800 tw-text-center tw-py-2 tw-px-4 tw-mx-4 tw-rounded-lg tw-mb-2 tw-flex tw-items-center tw-justify-center tw-gap-2">
              <span>{t('marketplace/messages/finishBanner')}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <ScenarioSide onSubmit={handleSubmit(onSubmit)}>
      {/* Header */}
      <div className="tw-h-14 tw-p-2 tw-flex tw-justify-between tw-items-center">
        <PSButton
          id="btn-message-close"
          onClick={() => {
            reset();
            setShowMessages(false);
          }}
          variant="none"
          className="tw-bg-white tw-shadow-sm tw-border tw-border-solid tw-border-gray-300"
          type="button"
        >
          <BiX className="tw-w-6 tw-h-6" />
        </PSButton>

        <div className="tw-flex tw-items-center tw-gap-3">
          <PSButton
            id="btn-message-finish"
            disabled={isDisabled}
            isLoading={updateStatusResult.isLoading}
            onClick={handleFinishClick}
            variant="outline"
            className="tw-bg-white tw-shadow-sm tw-border tw-border-gray-300 disabled:!tw-border-solid disabled:!tw-text-gray-500 disabled:!tw-cursor-not-allowed"
            type="button"
          >
            {t('marketplace/messages/finish')}
          </PSButton>
          <PSButton
            id="btn-message-dispute"
            disabled={isDisabled}
            isLoading={disputeResult.isLoading}
            onClick={handleDisputeClick}
            variant="outlineWarning"
            className="tw-bg-white tw-shadow-sm tw-border tw-border-gray-300 disabled:!tw-border-solid disabled:!tw-text-gray-500 disabled:!tw-cursor-not-allowed"
            type="button"
          >
            {t('marketplace/messages/dispute')}
          </PSButton>
        </div>
      </div>

      {/* Messages */}
      {content}

      {/* Input */}
      <div className="tw-flex tw-items-start tw-gap-2 tw-border-t tw-border-gray-200 tw-bg-white tw-p-4">
        <Form.Group className="tw-flex-1">
          <Form.Control
            as="textarea"
            rows={3}
            className="tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-border tw-border-gray-300 focus:tw-border-blue-500 focus:tw-ring-2 focus:tw-ring-blue-200 tw-resize-none"
            isInvalid={!!errors.message}
            placeholder={t('marketplace/messages/typeMessage')}
            maxLength={500}
            disabled={isDisabled}
            {...register('message')}
          />
          {errors.message && (
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.message.message}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <PSInput type="file" hidden accept={formatExtensions.join(',')} ref={fileInputRef} onChange={e => handleUpload(e.target.files?.[0] || null)} />

        <PSButton
          id="btn-message-attachment"
          isLoading={cdnUploadResults.isLoading}
          disabled={addMessageResult.isLoading || cdnUploadResults.isLoading || isDisabled}
          type="button"
          variant={'info'}
          icon={<BiPaperclip size={22} />}
          onClick={() => fileInputRef.current?.click()}
        />
        <PSButton
          id="btn-message-send"
          isLoading={addMessageResult.isLoading}
          disabled={addMessageResult.isLoading || isDisabled}
          type="submit"
          title={t('marketplace/messages/send')}
        />
      </div>
    </ScenarioSide>
  );
};
